import React from 'react';
import SalesDetailPage from './SalesDetailPage';
import { ADDON_COLUMNS, SORT_TYPES, getDefaultVisibleColumns, getDefaultHiddenColumns } from '../constants/addonsColumnsConfig';
import { buildSalesFilterGroups } from '../constants/salesFiltersConfig';
import { currencyFormatter, compactCurrency } from '../utils/reportingUtils';

const ADDONS_CONFIG = {
  pageTitle: 'Add-Ons',
  pageSubtitle: 'Add-on sales across your venues.',
  sidebarKey: 'sales_addons',
  defaultPreset: 'this_month',

  columns: ADDON_COLUMNS,
  sortTypes: SORT_TYPES,
  getDefaultVisibleColumns,
  getDefaultHiddenColumns,
  localStorageKey: (teamId) => `reportingSalesAddonColumns_${teamId}`,
  firstColumnId: 'add_on_name',

  salesDataKey: 'add_ons',
  venueSeriesKey: 'add_ons',
  detailEndpoint: (teamId) => `/teams/${teamId}/reporting/dashboard/sales-addons/data.json`,
  detailResponseKey: 'add_ons',
  exportBasePath: (teamId) => `/teams/${teamId}/reporting/dashboard/sales-addons/export`,
  salesExportSection: 'add_ons',
  detailErrorMessage: 'Failed to load add-on data.',
  detailTableTitle: 'Add-On Orders',
  emptyMessage: 'No add-ons found.',
  searchPlaceholder: 'Search add-ons...',
  paginationLabel: 'add-ons',
  itemLabel: (count) => `add-on${count !== 1 ? 's' : ''}`,

  buildKpiCards: (current, prior) => [
    { label: 'Add-On Revenue', value: compactCurrency(current.revenue || 0), current: current.revenue || 0, prior: prior.revenue || 0 },
    { label: 'Add-Ons Sold', value: (current.count || 0).toLocaleString(), current: current.count || 0, prior: prior.count || 0 },
  ],
  kpiGridColumns: 2,

  chartColor: '#8AC926',
  salesCardTitle: 'Add-Ons',
  buildMetricValue: (d) => currencyFormatter.format(d.revenue || 0),
  buildMetricLabel: (d) => `${(d.count || 0).toLocaleString()} add-ons sold`,
  overTimeKey: 'over_time',
  countLabel: 'add-ons',

  topCharts: [
    { title: 'Top 10 Events by Add-Ons Sold', dataKey: 'top_events_by_addon_count', barDataKey: 'addons_sold', barColor: '#8AC926', isCurrency: false, tooltipLabel: 'Add-Ons Sold' },
    { title: 'Top 10 Events by Add-On Revenue', dataKey: 'top_events_by_addon_revenue', barDataKey: 'revenue', barColor: '#8AC926', isCurrency: true, tooltipLabel: 'Add-On Revenue' },
  ],

  groupByOptions: [
    { key: 'event', label: 'Event' },
    { key: 'add_on', label: 'Add-On Order' },
  ],
  groupByDefault: 'event',
  eventGroupedColumns: [
    { key: 'event_name', label: 'Event' },
    { key: 'venue_name', label: 'Venue' },
    { key: 'event_date', label: 'Event Date' },
    { key: 'order_count', label: 'Orders' },
    { key: 'add_on_count', label: 'Add-Ons' },
    { key: 'face_value', label: 'Face Value' },
    { key: 'addon_fees', label: 'Add-on Fees' },
    { key: 'taxes', label: 'Taxes' },
    { key: 'total_revenue', label: 'Revenue' },
  ],

  renderEventCell: (key, row, { formatEventDate, currencyFormatter: cf }) => {
    switch (key) {
      case 'event_name': return row.event_name || '\u2014';
      case 'venue_name': return row.venue_name || '\u2014';
      case 'event_date': return <span className="text-nowrap">{row.event_date ? formatEventDate(row.event_date) : '\u2014'}</span>;
      case 'order_count': return row.order_count.toLocaleString();
      case 'add_on_count': return row.add_on_count.toLocaleString();
      case 'face_value': return cf.format(row.face_value || 0);
      case 'addon_fees': return cf.format(row.addon_fees || 0);
      case 'taxes': return cf.format(row.taxes || 0);
      case 'total_revenue': return cf.format(row.total_revenue || 0);
      default: return '\u2014';
    }
  },

  renderCellValue: (col, addOn, { formatEventDateTime, currencyFormatter: cf }) => {
    switch (col.id) {
      case 'add_on_name': return addOn.add_on_name || '\u2014';
      case 'event_name': return addOn.event_name || '\u2014';
      case 'venue_name': return addOn.venue_name || '\u2014';
      case 'customer_name': return addOn.customer_name || '\u2014';
      case 'customer_email': return addOn.customer_email || '\u2014';
      case 'amount': return cf.format(addOn.amount || 0);
      case 'qty': return (addOn.qty || 0).toLocaleString();
      case 'order_date': return <span className="text-nowrap">{formatEventDateTime(addOn.order_date)}</span>;
      case 'sold_by': return addOn.sold_by || '\u2014';
      case 'device_name': return addOn.device_name || '\u2014';
      default: return '\u2014';
    }
  },

  filterTitle: 'Filter Add-Ons',
  filterSubtitle: 'Apply filters to narrow add-on sales data.',
  buildFilterGroups: (venueOwnerships, eventOptions, tagOptions, sellerOptions) =>
    buildSalesFilterGroups(venueOwnerships, eventOptions, tagOptions, { sellerOptions }),
};

function ReportingSalesAddonsPage(props) {
  return <SalesDetailPage {...props} config={ADDONS_CONFIG} />;
}

export default ReportingSalesAddonsPage;
